/**
 * A storey's walls, doors and windows for the report's plans, so they are
 * drawn by the floor plan view's own `drawWalls`, `drawDoorsWithRotation` and
 * `drawWindows` (web-app/src/features/inspection-report/planDrawing.js) — the
 * user, 2026-10-16, on why the plans did not look like the floor plan: walls
 * with their thickness, doors with their swings, windows. Read from the
 * structure doc of the checkpoint the run inspected ("Post-Processing
 * Complete", the scan as found), the same checkpoint the rooms come from, and
 * frozen with the report: each element carries only the fields that drawing
 * reads. They are drawn as the floor plan's default ("bold") mode draws them,
 * so each room's own walls come too: the thin line on the room's face, over
 * the structure wall it is aligned to. Pure.
 */
const {
  matchesStory,
  buildWallStoryLookup,
  wallMatchesStory,
} = require('../geometry/storyMembership');
const { getWallDirection } = require('../geometry/wallGeometry');

// What the floor plan's wall and opening drawing reads of an element
// (`utils/elementDrawingUtils.js`, `utils/doorUtils.js`); the rest — the
// polygons, the alignment history, RoomPlan's ids — is left behind.
const WALL_FIELDS = [
  'id',
  'story',
  'category',
  'center',
  'dimensions',
  'rotation',
  // the outline's mitred corners (`geometry/wallGeometry.calculateWallFootprint`)
  'leftMiter',
  'rightMiter',
  'isUserCreated',
  'frontFacesSameDirection',
  'demoed',
];
// A room's own wall: its line on the room's face, and the structure wall it
// is aligned to, which the floor plan draws behind it.
const ROOM_WALL_FIELDS = [
  'id',
  'category',
  'center',
  'dimensions',
  'rotation',
  'demoed',
];
const ALIGNMENT_FIELDS = [
  'structureWallId',
  'bottomLeft',
  'bottomRight',
  'status',
];
const OPENING_FIELDS = [
  'id',
  'story',
  'category',
  'center',
  'dimensions',
  'rotation',
  'parentWallIds',
  'wallFacesPriorityRoom',
  'doorType',
  'slidingPanelCount',
  'innerHeight',
  'demoed',
];

/** `el` with only `fields`, and none that is undefined (Firestore stores it). */
const pick = (el, fields) =>
  Object.fromEntries(
    fields.filter((f) => el[f] !== undefined).map((f) => [f, el[f]])
  );

/**
 * A structure's walls / doors / windows / openings: the canonical
 * `structure<Kind>` names first, else the unprefixed ones every real doc
 * carries (web-app/CLAUDE.md).
 */
function listOf(structure, kind) {
  const canonical =
    structure[`structure${kind.charAt(0).toUpperCase()}${kind.slice(1)}`];
  if (Array.isArray(canonical)) return canonical;
  return Array.isArray(structure[kind]) ? structure[kind] : [];
}

const usable = (el) => el && el.id && el.center && el.dimensions;

/** A room's wall with what the floor plan draws it from. */
function roomWall(wall) {
  const out = pick(wall, ROOM_WALL_FIELDS);
  if (wall.alignment) out.alignment = pick(wall.alignment, ALIGNMENT_FIELDS);
  return out;
}

/**
 * An opening placed on its wall's centre line, where it is along the wall,
 * when it touches the wall at all — the floor plan draws an opening at its own
 * centre, and a window stored on the wall's face (or a hair beyond it) draws
 * half a wall to one side. An opening that does not reach its wall, or whose
 * wall is not among `wallsById`, is left where it is: moving it would be
 * guessing.
 */
function onItsWall(opening, wallsById) {
  const wall = wallsById.get((opening.parentWallIds || [])[0]);
  if (!wall) return opening;
  const dir = getWallDirection(wall);
  const len = Math.hypot(dir.x, dir.z);
  if (!(len > 0)) return opening;
  const ax = dir.x / len;
  const az = dir.z / len;
  const dx = opening.center.x - wall.center.x;
  const dz = opening.center.z - wall.center.z;
  const along = dx * ax + dz * az;
  const across = -dx * az + dz * ax;
  const reach = ((wall.dimensions.z || 0) + (opening.dimensions.z || 0)) / 2;
  if (!across || Math.abs(across) > reach) return opening;
  return {
    ...opening,
    center: {
      ...opening.center,
      x: wall.center.x + ax * along,
      z: wall.center.z + az * along,
    },
  };
}

/**
 * The walls, doors, windows and openings on `story` (null: a structure with
 * no storeys), by the floor plan's rule: an unstamped element is on every
 * storey, and a wall a room stands on from another storey is on that one too.
 * `rooms`: the checkpoint's rooms. → { walls, roomWalls, doors, windows,
 * openings }, or null with no structure.
 */
function storeyStructure(structure, rooms, story) {
  if (!structure) return null;
  const walls = listOf(structure, 'walls').filter(usable);
  const lookup = buildWallStoryLookup(rooms, walls);
  const wallsById = new Map(walls.map((w) => [w.id, w]));
  const openingsOf = (kind) =>
    listOf(structure, kind)
      .filter((o) => usable(o) && matchesStory(o, story))
      .map((o) => onItsWall(pick(o, OPENING_FIELDS), wallsById));
  return {
    walls: walls
      .filter((w) => wallMatchesStory(w, story, lookup))
      .map((w) => pick(w, WALL_FIELDS)),
    roomWalls: (rooms || [])
      .filter((r) => matchesStory(r, story))
      .flatMap((r) => (r.walls || []).filter(usable).map(roomWall)),
    doors: openingsOf('doors'),
    windows: openingsOf('windows'),
    openings: openingsOf('openings'),
  };
}

/**
 * Does an element reach into `frame` (`{ minX, maxX, minZ, maxZ }`, metres)?
 * It is taken as the circle round its centre that holds it at any turn, so
 * the test needs no rotation and errs on keeping one: the plan's own frame
 * clips what it draws.
 */
function reaches(el, frame) {
  const { x, z } = el.center;
  const r =
    Math.hypot(el.dimensions.x || 0, el.dimensions.z || 0) / 2 ||
    Number.EPSILON;
  const dx = Math.max(frame.minX - x, 0, x - frame.maxX);
  const dz = Math.max(frame.minZ - z, 0, z - frame.maxZ);
  return Math.hypot(dx, dz) <= r;
}

/** A storey's drawing cut to what reaches into a room plan's frame, or null with none. */
function inFrame(drawing, frame) {
  if (!drawing) return null;
  const keep = (list) => list.filter((el) => reaches(el, frame));
  return {
    walls: keep(drawing.walls),
    roomWalls: keep(drawing.roomWalls),
    doors: keep(drawing.doors),
    windows: keep(drawing.windows),
    openings: keep(drawing.openings),
  };
}

module.exports = { storeyStructure, inFrame };
